/**
 * Login Page Component
 * Reachable in local mode too: signing in is how a phone's chores get synced and shared.
 */

import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { AuthLayout } from './auth/AuthLayout';
import { OAuthButtons } from './auth/OAuthButtons';
import { CodeDialog } from './CodeDialog';
import { Alert, Button } from './ui';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAccount, isLocal, canCreateAccount, accountRefused, clearAccountRefused, redeemCode } = useAuth();
  const [codeOpen, setCodeOpen] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  // Redirect to dashboard once signed into an account (a local user is "authenticated" too)
  useEffect(() => {
    if (isAccount) {
      navigate('/dashboard');
    }
  }, [isAccount, navigate]);

  const handleSuccess = () => {
    navigate('/dashboard');
  };

  const handleRedeemed = () => {
    clearAccountRefused();
    setRedeemed(true);
  };

  return (
    <AuthLayout
      title="Sign in to sync"
      subtitle="Chores Champ works on this phone without an account. Sign in to keep your chores on all your devices and share a household. What you've made here comes with you."
    >
      <div className="space-y-6">
        {accountRefused && (
          <Alert
            tone="warning"
            action={<Button size="sm" onClick={() => setCodeOpen(true)}>Enter a code</Button>}
          >
            {canCreateAccount
              // Refused by the server though the phone has a code: the server allows by the
              // email the code was made for, and this Google account has another one.
              ? <>There&apos;s no Chores Champ account for that Google account yet, and your code is for another email. Sign in with the Google account the code was made for, or ask for a code for this one.</>
              : <>There&apos;s no Chores Champ account for that Google account yet, and creating one takes a code.</>}
          </Alert>
        )}
        {redeemed && !accountRefused && (
          <Alert tone="success">Code accepted. Sign in with Google again to create your account.</Alert>
        )}

        <OAuthButtons onSuccess={handleSuccess} />

        {isLocal && (
          <Button variant="ghost" block onClick={() => navigate('/dashboard')}>
            Continue without an account
          </Button>
        )}

        {canCreateAccount === false && !accountRefused && (
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            No account yet? Creating one takes a code.{' '}
            <button
              type="button"
              onClick={() => setCodeOpen(true)}
              className="rounded font-medium text-indigo-600 hover:text-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Enter a code
            </button>
          </p>
        )}
        {canCreateAccount === true && !redeemed && (
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            You have a code, so signing in with Google creates your account if you don&apos;t have one yet.
          </p>
        )}
      </div>

      <CodeDialog open={codeOpen} onClose={() => setCodeOpen(false)} onRedeem={redeemCode} onRedeemed={handleRedeemed} />
    </AuthLayout>
  );
}
